import { FilePdf } from "@phosphor-icons/react";
import api from "../../lib/api";
import { Field, Area, Section, Radio } from "./fields";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const blankRow = () => ({ name: "", dosage: "", frequency: "" });

export const emptyForm3 = {
  date_reviewed: "", entries: Array.from({ length: 5 }, blankRow), notes: "", source: "", source_other: "",
};

const SOURCES = ["Client/representative provided", "Current written medication list", "Medication containers reviewed", "Other"];

export default function Form3Medications({ data, onChange, clientId, clientCode, publicMode }) {
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setR = (k) => (v) => onChange({ ...data, [k]: v });
  const setRow = (i, k, v) => onChange({ ...data, entries: data.entries.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) });
  const addRow = () => onChange({ ...data, entries: [...data.entries, blankRow()] });

  return (
    <div className="space-y-6" data-testid="form3-form">
      <div className="rounded-lg border border-stone-200 bg-accent p-4 text-sm text-stone-600" data-testid="form3-note">
        {publicMode
          ? "Please list current medications, vitamins, and supplements — including over-the-counter ones. Our caregivers provide reminders only; they never give medical advice or adjust doses."
          : "Kept separate so it can be updated any time without reopening the assessment. Caregivers provide reminders only — no medical management."}
      </div>

      {clientId && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <span className="text-sm text-stone-500">Client:</span>
          <code className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground" data-testid="form3-client-code">{clientCode}</code>
          <a href={`${api.defaults.baseURL}/clients/${clientId}/forms/medications/pdf`} download
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="form3-pdf-button">
            <FilePdf size={14} /> Download PDF
          </a>
        </div>
      )}

      <Section title="Medication List">
        <Field label="Date reviewed" tid="f3-date-reviewed" type="date" value={data.date_reviewed} onChange={set("date_reviewed")} />
        <div className="md:col-span-2">
          <div className="space-y-2.5">
            <div className="grid grid-cols-[28px_1fr_120px_120px] items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-400">
              <span>#</span><span>Medication name</span><span>Dosage</span><span>Frequency</span>
            </div>
            {(data.entries || []).map((r, i) => (
              <div key={i} className="grid grid-cols-[28px_1fr_120px_120px] items-center gap-2">
                <span className="text-xs text-stone-400">{i + 1}</span>
                <Input value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`f3-med-name-${i}`} />
                <Input value={r.dosage} onChange={(e) => setRow(i, "dosage", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`f3-med-dosage-${i}`} />
                <Input value={r.frequency} onChange={(e) => setRow(i, "frequency", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`f3-med-frequency-${i}`} />
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRow} className="mt-3 border-stone-300 text-stone-700 hover:bg-accent" data-testid="f3-add-row">
            + Add medication row
          </Button>
        </div>
        <div className="md:col-span-2"><Area label="Additional medication notes" tid="f3-notes" value={data.notes} onChange={set("notes")} /></div>
        <Radio label="Medication list source" options={SOURCES} value={data.source} onChange={setR("source")} tid="f3-source" />
        {data.source === "Other" && (
          <Field label="Other source" tid="f3-source-other" value={data.source_other} onChange={set("source_other")} />
        )}
      </Section>
    </div>
  );
}
